// ✅ Phase 5: SetupWizard — the step-by-step tour, in the browser.
// Every answer is saved SEMANTICALLY: the wizard sends it through the real
// owner-chat engine (mode='owner'), whose tools persist it to the right node.
// The live test (DemoChat) runs HERE in the browser — WhatsApp connects LAST,
// only when the customer loves the service.
import React, { useState, useEffect, useRef } from 'react';
import DemoChat from './DemoChat';

interface WizardStep {
  key: string;
  icon: string;
  title: string;
  hint: string;
  placeholder?: string;
  kind: 'intro' | 'text' | 'demo' | 'final' | 'choice';
}

const STEPS: WizardStep[] = [
  { key: 'choice', icon: '📱', title: 'මොන වගේ agent එකක්ද ඕන?', hint: 'මුලින්ම තෝරන්න — පස්සේ ඕන වේලාවක වෙනස් කරන්න/WhatsApp setup එක run කරන්න පුළුවන්.', kind: 'choice' },
  { key: 'welcome', icon: '👋', title: 'Luna ට සාදරයෙන් පිළිගන්නවා!', hint: 'මිනිත්තු 3කින් ඔයාගේ business එකට මාව සූදානම් කරමු. පියවර කීහිපයයි — ඕන වේලාවක skip කරන්න පුළුවන්.', kind: 'intro' },
  { key: 'business', icon: '🏪', title: 'ඔයාගේ business එක', hint: 'බිස්නස් එකේ නම සහ මොන වගේද කියන්න (උදා: "Nisha Fashion — clothing store, Nugegoda")', placeholder: 'Nisha Fashion — clothing store…', kind: 'text' },
  { key: 'knowledge', icon: '📚', title: 'Business එකේ සම්පූර්ණ විස්තර', hint: 'මොනවා විකුණනවද, policies, delivery, offers, වැඩ කරන වේලාවන් — හැම දෙයක්ම ලියන්න. Luna මේවා තේරුම් අරගෙන මතක තියාගන්නවා.', placeholder: 'අපි women\'s clothing විකුණනවා. T-shirts, jeans, dresses…', kind: 'text' },
  { key: 'persona', icon: '🎭', title: 'Luna කතා කරන විදිහ', hint: 'Luna ගේ හැසිරීම ඔයා කැමති විදිහට (උදා: "හිනාමයි මිත්‍ර, Sinhala-English mix, short replies")', placeholder: 'හිනාමයි මිත්‍රයි, short replies, Sinhala-English mix…', kind: 'text' },
  { key: 'products', icon: '🛍️', title: 'Products එකතු කරමු', hint: 'Product එකක නම + මිල + sizes ලියන්න (පිළිවෙලට: "T-Shirt Rs.2500 — S,M,L,XL"). පස්සේ WhatsApp එකෙන් photos එකතු කරන්න පුළුවන්.', placeholder: 'Blue Denim T-Shirt Rs.2500 — S,M,L,XL', kind: 'text' },
  { key: 'media', icon: '🖼️', title: 'Media Vault එක', hint: 'Shop එකේ/products වල photos තියෙනවා නම් URL/විස්තර දෙන්න. දැනට පුළුවන් තරම් හරි — පස්සේ WhatsApp එකෙන් එවන්නත් පුළුවන්.', placeholder: 'Shop photos, product photos… (URL/notes)', kind: 'text' },
  { key: 'live_test', icon: '🎬', title: 'Magic Live Test', hint: 'දැන් මැජික් එක — ඔයාගේ agent මෙතැනදීම live test කරමු. WhatsApp එකක් තවම ඕන නෑ!', kind: 'demo' },
  { key: 'final', icon: '🚀', title: 'ඔයාගේ Luna ready!', hint: 'Service එක හොඳයි කියලා හිතෙනවා නම් අන්තිමට WhatsApp connect කරන්න. එච්චරයි!', kind: 'final' },
];

function authHeaders(token?: string): Record<string, string> {
  const h: Record<string, string> = { 'Content-Type': 'application/json' };
  const t = token || (typeof window !== 'undefined' ? localStorage.getItem('tenet_auth_token') : null);
  if (t) h['Authorization'] = t.startsWith('Bearer ') ? t : `Bearer ${t}`;
  return h;
}

export default function SetupWizard({ token, onFinished }: { token?: string; onFinished: () => void }) {
  const [stepIndex, setStepIndex] = useState(0);
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [savedNote, setSavedNote] = useState<string | null>(null);
  const [simStatus, setSimStatus] = useState<any>(null);
  const [choiceSaving, setChoiceSaving] = useState(false);
  const [waNumber, setWaNumber] = useState('');
  const [uploadedUrls, setUploadedUrls] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const step = STEPS[stepIndex];
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    fetch('/api/tenet/onboarding/status', { headers: authHeaders(token), cache: 'no-store' })
      .then((r) => r.json())
      .then((j) => {
        const done: string[] = j?.state?.completed || [];
        // resume: first step whose key isn't completed (welcome never blocks)
        const idx = STEPS.findIndex((s) => s.key !== 'welcome' && !done.includes(s.key));
        if (idx > 0) setStepIndex(idx);
      })
      .catch(() => {});
    fetch('/api/tenet/simulation/status', { headers: authHeaders(token), cache: 'no-store' })
      .then((r) => r.json())
      .then((j) => setSimStatus(j))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const markStep = async (key: string) => {
    try {
      await fetch('/api/tenet/onboarding/advance', {
        method: 'POST',
        headers: authHeaders(token),
        body: JSON.stringify({ step: key }),
      }).catch(() => {});
    } catch { /* progress is best-effort */ }
  };


  const chooseSetup = async (choice: 'whatsapp_agent' | 'other') => {
    setChoiceSaving(true);
    try {
      const r = await fetch('/api/tenet/onboarding/setup-choice', {
        method: 'POST',
        headers: authHeaders(token),
        body: JSON.stringify({ choice, whatsappNumber: waNumber.trim() }),
      });
      const j = await r.json();
      setSavedNote(j?.message || (choice === 'whatsapp_agent' ? '📱 WhatsApp Agent Setup ✅' : '✨ Fresh start ✅'));
    } catch {
      setSavedNote('⚠️ තෝරාගැනීම save කරන්න බැරි වුණා — නැවත උත්සාහ කරන්න.');
      setChoiceSaving(false);
      return;
    }
    setChoiceSaving(false);
    await markStep('choice');
    setValue('');
    if (stepIndex < STEPS.length - 1) setStepIndex(stepIndex + 1);
    else onFinished();
  };

  const saveAndNext = async () => {
    setSaving(true);
    setSavedNote(null);
    const content = value.trim();
    if (content && step.kind === 'text') {
      // Semantic save: send through the REAL owner-chat engine — its tools
      // persist this to the right node (knowledge/persona/product/media).
      const prefixes: Record<string, string> = {
        business: 'මේ මගේ business එකේ විස්තරයි — මතක තියාගන්න: ',
        knowledge: 'මේ මගේ business එකේ සම්පූර්ණ knowledge එකයි — knowledge base එකට save කරන්න: ',
        persona: 'දැන් සිට Luna ගේ persona/හැසිරීම මේ විදිහට වෙනස් කරන්න: ',
        products: 'අලුත් product එකක් add කරන්න (images එක්ක): ',
        media: 'මේ media තොරතුරු media vault එකට save කරන්න: ',
      };
      const imgSuffix = step.key === 'products' && uploadedUrls.length ? ` | product images: ${uploadedUrls.join(', ')}` : '';
      try {
        await fetch('/api/tenet/whatsapp/test-chat', {
          method: 'POST',
          headers: authHeaders(token),
          body: JSON.stringify({ message: (prefixes[step.key] || '') + content + imgSuffix, mode: 'owner' }),
        });
        setSavedNote('✅ save කළා — Luna තේරුම් අරගෙන store කළා.');
      } catch {
        setSavedNote('⚠️ save කරන්න බැරි වුණා — පස්සේ නැවත උත්සාහ කරන්න.');
      }
    }
    await markStep(step.key);
    setSaving(false);
    setValue('');
    setUploadedUrls([]);
    if (stepIndex < STEPS.length - 1) setStepIndex(stepIndex + 1);
    else onFinished();
  };

  const skip = async () => {
    await markStep(step.key);
    setValue('');
    if (stepIndex < STEPS.length - 1) setStepIndex(stepIndex + 1);
    else onFinished();
  };

  return (
    <div style={{ width: '100%', maxWidth: 720, margin: '0 auto', padding: '20px 16px 40px' }}>
      {/* escape hatch — never trap an existing customer in the tour */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
        <button className="lg-btn" style={{ padding: '6px 12px', fontSize: 11.5 }} onClick={onFinished}>
          Tour එක පස්සේ — dashboard එකට යමු →
        </button>
      </div>
      {/* progress */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 22 }}>
        {STEPS.map((s, i) => (
          <div key={s.key} style={{
            flex: 1, height: 6, borderRadius: 999,
            background: i < stepIndex ? 'linear-gradient(90deg,#38BDF8,#2563EB)' : i === stepIndex ? 'rgba(56,189,248,0.55)' : 'rgba(125,211,252,0.14)',
            boxShadow: i === stepIndex ? '0 0 14px rgba(56,189,248,0.5)' : 'none',
            transition: 'background 0.3s ease',
          }} />
        ))}
      </div>

      <div className="liquid-card-3d" style={{ padding: '30px 26px' }}>
        <div style={{ fontSize: 40, marginBottom: 10 }}>{step.icon}</div>
        <h2 className="lg-glow-text" style={{ fontSize: 24, fontWeight: 900, margin: '0 0 8px' }}>{step.title}</h2>
        <p style={{ color: '#A9C7E8', fontSize: 14.5, lineHeight: 1.65, margin: '0 0 20px' }}>{step.hint}</p>

        {step.kind === 'choice' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div className="liquid-card-3d" onClick={() => !choiceSaving && chooseSetup('whatsapp_agent')} style={{ padding: 14, cursor: 'pointer', borderColor: 'rgba(56,189,248,0.5)', display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ fontSize: 22, width: 40, height: 40, borderRadius: 12, background: 'rgba(56,189,248,0.16)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>📱</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 800, fontSize: 14, color: '#E6F4FF' }}>WhatsApp Agent Setup</div>
                <div style={{ fontSize: 11.5, color: '#A9C7E8' }}>nodes auto-wire · WhatsApp අන්තිමට</div>
              </div>
              <div style={{ color: '#7DD3FC', fontSize: 16 }}>→</div>
            </div>
            <div className="liquid-card-3d" onClick={() => !choiceSaving && chooseSetup('other')} style={{ padding: 14, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ fontSize: 22, width: 40, height: 40, borderRadius: 12, background: 'rgba(125,211,252,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>✨</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 800, fontSize: 14, color: '#E6F4FF' }}>Other — fresh start</div>
                <div style={{ fontSize: 11.5, color: '#A9C7E8' }}>WhatsApp setup පස්සේ ඕන වේලාවක්</div>
              </div>
              <div style={{ color: '#7DD3FC', fontSize: 16 }}>→</div>
            </div>
            <input
              value={waNumber}
              onChange={(e) => setWaNumber(e.target.value)}
              placeholder="WhatsApp number (optional)"
              style={{ width: '100%', boxSizing: 'border-box', padding: '9px 12px', borderRadius: 12, border: '1px solid rgba(125,211,252,0.22)', background: 'rgba(10,22,40,0.5)', color: '#E6F4FF', fontSize: 12, outline: 'none' }}
            />
            {choiceSaving && <div style={{ color: '#7DD3FC', fontSize: 11.5 }}>saving…</div>}
          </div>
        )}

        {step.kind === 'text' && step.key === 'products' && (
          <div style={{ marginTop: 10 }}>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '9px 14px', borderRadius: 12, border: '1px dashed rgba(125,211,252,0.45)', color: '#7DD3FC', fontSize: 12, cursor: 'pointer' }}>
              {uploading ? 'uploading…' : '📷 Product photos එකතු කරන්න (දැන්ම)'}
              <input type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={async (e) => {
                const files = Array.from(e.target.files || []);
                if (!files.length) return;
                setUploading(true);
                for (const f of files) {
                  try {
                    const fd = new FormData();
                    fd.append('file', f);
                    const uploadHeaders = authHeaders(token); delete uploadHeaders['Content-Type'];
                    const r = await fetch('/api/admin/luna-flows/upload-asset', { method: 'POST', headers: uploadHeaders, body: fd });
                    const j = await r.json();
                    const url = j?.url || j?.fileUrl || j?.data?.url || (Array.isArray(j?.files) ? j.files[0]?.url : '') || '';
                    if (url) setUploadedUrls((u) => [...u, url]);
                  } catch { /* skip failed */ }
                }
                setUploading(false);
              }} />
            </label>
            {uploadedUrls.length > 0 && (
              <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                {uploadedUrls.map((u, i) => (
                  <img key={i} src={u} alt="product" style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 12, border: '1px solid rgba(125,211,252,0.35)' }} />
                ))}
              </div>
            )}
          </div>
        )}

        {step.kind === 'text' && (
          <textarea
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={step.placeholder}
            rows={4}
            style={{ width: '100%', padding: 16, borderRadius: 16, border: '1px solid rgba(125,211,252,0.28)', background: 'rgba(10,22,40,0.55)', color: '#E6F4FF', fontSize: 14, lineHeight: 1.6, outline: 'none', resize: 'vertical', boxSizing: 'border-box' }}
          />
        )}

        {step.kind === 'demo' && (
          <div style={{ marginTop: 6 }}>
            <DemoChat token={token} />
          </div>
        )}

        {step.kind === 'final' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div className="liquid-card-3d" style={{ padding: 16, borderRadius: 16 }}>
              <div style={{ fontWeight: 800, color: '#E6F4FF', marginBottom: 6 }}>🧪 Simulation progress</div>
              <div style={{ height: 8, borderRadius: 999, background: 'rgba(125,211,252,0.15)', overflow: 'hidden' }}>
                <div style={{ width: `${simStatus?.percent ?? 0}%`, height: '100%', background: 'linear-gradient(90deg,#38BDF8,#2563EB)', borderRadius: 999 }} />
              </div>
              <div style={{ fontSize: 12, color: '#A9C7E8', marginTop: 6 }}>{simStatus ? `${simStatus.messageCount}/${simStatus.target} messages (${simStatus.percent}%) · Real testing: ${simStatus.realTestingEnabled ? 'ON ✅' : 'off'}` : 'loading…'}</div>
            </div>
            <div className="liquid-card-3d" style={{ padding: 16, borderRadius: 16 }}>
              <div style={{ fontWeight: 800, color: '#E6F4FF', marginBottom: 6 }}>📱 WhatsApp — අන්තිමට connect කරන්න</div>
              <div style={{ fontSize: 13, color: '#A9C7E8', lineHeight: 1.6, marginBottom: 12 }}>
                ඔයාගේ business WhatsApp එක connect කරන්නේ <b style={{ color: '#7DD3FC' }}>සර්විස් එක ඔයාට හොඳයි කියලා හිතුණම</b>. දැනට browser එකේ වැඩ කරන්න පුළුවන්!
              </div>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <button className="lg-btn lg-btn-primary" onClick={onFinished}>🚀 Dashboard එකට යමු</button>
                <button className="lg-btn" onClick={onFinished}>පස්සේ connect කරමු</button>
              </div>
            </div>
          </div>
        )}

        {savedNote && <div style={{ marginTop: 12, fontSize: 12.5, color: '#7DD3FC' }}>{savedNote}</div>}

        {step.kind !== 'demo' && step.kind !== 'final' && step.kind !== 'choice' && (
          <div style={{ display: 'flex', gap: 10, marginTop: 22, flexWrap: 'wrap' }}>
            <button className="lg-btn lg-btn-primary" style={{ minWidth: 150 }} onClick={saveAndNext} disabled={saving}>
              {saving ? 'saving…' : stepIndex === 0 ? 'පටන් ගමු →' : 'ඊළඟට →'}
            </button>
            {stepIndex > 0 && <button className="lg-btn" onClick={skip}>skip</button>}
          </div>
        )}
        {(step.kind === 'demo' || step.kind === 'choice') && (
          <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
            <button className="lg-btn lg-btn-primary" style={{ minWidth: 150 }} onClick={saveAndNext}>ඊළඟට →</button>
            <button className="lg-btn" onClick={skip}>skip</button>
          </div>
        )}
      </div>
    </div>
  );
}
